import React from "react";
import {
  Anchor,
  Badge,
  Box,
  Divider,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconClockHour4,
} from "@tabler/icons-react";
import {
  TxLookupResult,
  formatDuration,
  formatRate,
  formatUtc,
} from "../helpers/txLookup";
import {
  formatBtc,
  formatSats,
  formatUsd,
} from "../../checkBitcoinAddress/helpers/addressLookup";
import SpeedUpWizard, { WizardState } from "./SpeedUpWizard";

const PANEL_STYLE = {
  backgroundColor: "rgba(0, 0, 0, 0.35)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
};
const MONO = { fontFamily: "monospace", fontVariantNumeric: "tabular-nums" as const };
const FINAL_CONFIRMATIONS = 6;
const OUTPUTS_LISTED = 12;

type Tone = "green" | "teal" | "yellow" | "orange" | "red" | "gray";

interface VerdictText {
  tone: Tone;
  title: string;
  detail: string;
}

/** The one-line answer at the top: confirmed, how soon, or stuck. */
export function verdict(tx: TxLookupResult): VerdictText {
  if (tx.confirmed) {
    const n = tx.confirmations ?? 1;
    const when = tx.block_time ? ` on ${formatUtc(tx.block_time)}` : "";
    const block = tx.block_height !== null ? ` in block ${tx.block_height.toLocaleString("en-US")}` : "";
    return {
      tone: "green",
      title: `Confirmed: ${n.toLocaleString("en-US")} confirmation${n === 1 ? "" : "s"}`,
      detail:
        `Mined${block}${when}. ` +
        (n >= FINAL_CONFIRMATIONS
          ? "That's final for practically every wallet and exchange."
          : `Most exchanges treat ${FINAL_CONFIRMATIONS} confirmations as final: about ${(FINAL_CONFIRMATIONS - n) * 10} more minutes.`),
    };
  }

  const rate = tx.effective_fee_rate ?? tx.fee_rate;
  const fees = tx.fees;
  if (fees && rate !== null && rate < fees.minimum) {
    return {
      tone: "red",
      title: "Unconfirmed, and its fee is below what most nodes accept",
      detail: `It pays ${formatRate(rate)}; the lowest fee rate nodes keep right now is about ${formatRate(fees.minimum)}. It may never confirm and can be dropped. Speed it up below, or wait for it to drop out of the mempool.`,
    };
  }

  const queue = tx.queue;
  if (!queue) {
    return {
      tone: "gray",
      title: "Unconfirmed: waiting in the mempool",
      detail: "It's been broadcast but isn't in a block yet. We couldn't load the current fee market, so there's no time estimate.",
    };
  }
  if (queue.blocks_away === 1 && !queue.or_more) {
    return {
      tone: "teal",
      title: "Unconfirmed: should confirm in the next block",
      detail: `Its fee rate (${formatRate(rate)}) is enough for the next block, which takes about 10 minutes on average. Nothing to do but wait.`,
    };
  }
  if (queue.blocks_away <= 3 && !queue.or_more) {
    return {
      tone: "yellow",
      title: `Unconfirmed: about ${queue.blocks_away} blocks away`,
      detail: `With its fee rate (${formatRate(rate)}) it should confirm in roughly ${queue.blocks_away * 10} minutes, if fees don't rise. To be sure of the next block, speed it up below.`,
    };
  }
  return {
    tone: "orange",
    title: queue.or_more
      ? "Stuck: it's behind most of the mempool"
      : `Stuck: about ${queue.blocks_away} blocks away`,
    detail: queue.or_more
      ? `Its fee rate (${formatRate(rate)}) is lower than the next several blocks need. It could take hours or days, and only confirms when fees drop. You can speed it up below.`
      : `Its fee rate (${formatRate(rate)}) puts it about ${queue.blocks_away} blocks back, roughly ${formatDuration(queue.blocks_away * 600)} if fees don't rise. You can speed it up below.`,
  };
}

const TONE_ICON: Record<Tone, React.ReactNode> = {
  green: <IconCircleCheck size={22} />,
  teal: <IconClockHour4 size={22} />,
  yellow: <IconClockHour4 size={22} />,
  orange: <IconAlertTriangle size={22} />,
  red: <IconAlertTriangle size={22} />,
  gray: <IconClockHour4 size={22} />,
};

const Fact: React.FC<{ label: string; value: React.ReactNode; sub?: React.ReactNode }> = ({ label, value, sub }) => (
  <Box>
    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
      {label}
    </Text>
    <Text c="white" size="sm" style={MONO}>
      {value}
    </Text>
    {sub && (
      <Text size="xs" c="dimmed" style={MONO}>
        {sub}
      </Text>
    )}
  </Box>
);

const shortId = (value: string) => `${value.slice(0, 10)}…${value.slice(-8)}`;

const TxResult: React.FC<{
  tx: TxLookupResult;
  watching: boolean;
  lastBlockTime: number | null;
  wizard: WizardState;
  onWizardChange: (state: WizardState) => void;
  onCheckTxid: (txid: string) => void;
  shareUrl: string;
}> = ({ tx, watching, lastBlockTime, wizard, onWizardChange, onCheckTxid, shareUrl }) => {
  const v = verdict(tx);
  const usd = (sats: number) => formatUsd(sats, tx.btc_usd_price);
  const nowSeconds = Date.now() / 1000;
  const effective = tx.package && tx.package.ancestor_count > 0 ? tx.package.effective_fee_rate : null;
  const needsSpeedUp =
    !tx.confirmed && !tx.coinbase && !!tx.advice && !(tx.queue && tx.queue.blocks_away === 1 && !tx.queue.or_more);
  // once someone is part-way through, a re-check that moves it up the queue doesn't pull the steps away
  const showWizard = needsSpeedUp || (!tx.confirmed && !tx.coinbase && !!tx.advice && wizard.role !== null);

  return (
    <Stack gap="md">
      <Paper
        p="md"
        radius="md"
        style={{ ...PANEL_STYLE, borderLeft: `4px solid var(--mantine-color-${v.tone}-6)` }}
      >
        <Group gap="sm" wrap="nowrap" align="flex-start">
          <Box c={`${v.tone}.5`} style={{ flexShrink: 0, marginTop: 2 }}>
            {TONE_ICON[v.tone]}
          </Box>
          <Stack gap={4} style={{ minWidth: 0 }}>
            <Text c="white" fw={700} size="lg" lh={1.3}>
              {v.title}
            </Text>
            <Text size="sm" c="dimmed">
              {v.detail}
            </Text>
            {!tx.confirmed && tx.first_seen && (
              <Text size="sm" c="dimmed">
                Waiting for {formatDuration(nowSeconds - tx.first_seen)} (first seen{" "}
                {formatUtc(tx.first_seen)}).
                {nowSeconds - tx.first_seen > 7 * 86400 &&
                  " Most nodes drop a transaction that's still unconfirmed after 2 weeks; the coins then stay where they were."}
              </Text>
            )}
            {watching && (
              <Text size="xs" c="teal.4">
                Watching: this page checks again by itself each time a new block is found
                {lastBlockTime ? ` (last block ${formatDuration(nowSeconds - lastBlockTime)} ago)` : ""}.
              </Text>
            )}
          </Stack>
        </Group>
      </Paper>

      <Paper p="md" radius="md" style={PANEL_STYLE}>
        <Stack gap="md">
          <Text size="xs" c="dimmed" style={{ ...MONO, overflowWrap: "anywhere" }}>
            {tx.txid}
          </Text>
          <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="md" verticalSpacing="md">
            <Fact
              label="Fee paid"
              value={tx.coinbase ? "none (new coins)" : formatSats(tx.fee_sats)}
              sub={tx.coinbase ? undefined : usd(tx.fee_sats)}
            />
            <Fact
              label="Fee rate"
              value={formatRate(tx.fee_rate)}
              sub={effective !== null ? `${formatRate(effective)} with its unconfirmed parents` : undefined}
            />
            <Fact label="Size" value={`${tx.vsize.toLocaleString("en-US")} vB`} />
            <Fact label="Amount moved" value={formatBtc(tx.output_sats)} sub={usd(tx.output_sats)} />
            <Fact
              label="Inputs → outputs"
              value={`${tx.input_count} → ${tx.output_count}`}
            />
            <Fact
              label="Replace-by-fee"
              value={tx.coinbase ? "–" : tx.rbf_signaled ? "Signals RBF" : "No signal"}
            />
          </SimpleGrid>

          {!tx.confirmed && tx.fees && (
            <>
              <Divider color="rgba(255,255,255,0.08)" label="Recommended fee rates right now" labelPosition="left" />
              <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="xs">
                {[
                  ["High priority", tx.fees.fastest],
                  ["Medium", tx.fees.half_hour],
                  ["Low", tx.fees.hour],
                  ["No priority", tx.fees.economy],
                ].map(([label, rate]) => (
                  <Paper key={label as string} p="xs" radius="sm" style={{ backgroundColor: "rgba(255,255,255,0.03)" }}>
                    <Text size="xs" c="dimmed">
                      {label}
                    </Text>
                    <Text size="sm" c="white" style={MONO}>
                      {formatRate(rate as number)}
                    </Text>
                  </Paper>
                ))}
              </SimpleGrid>
            </>
          )}
        </Stack>
      </Paper>

      {showWizard && (
        <SpeedUpWizard tx={tx} state={wizard} onChange={onWizardChange} onCheckTxid={onCheckTxid} shareUrl={shareUrl} />
      )}

      <Paper p="md" radius="md" style={PANEL_STYLE}>
        <Stack gap="xs">
          <Divider color="rgba(255,255,255,0.08)" label={`Outputs (${tx.output_count})`} labelPosition="left" />
          {tx.outputs.slice(0, OUTPUTS_LISTED).map((output, i) => (
            <Group key={i} justify="space-between" gap="xs" wrap="nowrap">
              <Text size="xs" style={{ ...MONO, minWidth: 0, overflowWrap: "anywhere" }}>
                {output.address ? (
                  <Anchor href={`/check_bitcoin_address?address=${encodeURIComponent(output.address)}`} c="gray.4" size="xs">
                    {output.address}
                  </Anchor>
                ) : (
                  <Text span c="dimmed" size="xs">
                    {output.type === "op_return" ? "OP_RETURN (data, no address)" : output.type}
                  </Text>
                )}
              </Text>
              <Text size="xs" c="white" style={{ ...MONO, whiteSpace: "nowrap" }}>
                {formatBtc(output.sats)}
              </Text>
            </Group>
          ))}
          {tx.output_count > OUTPUTS_LISTED && (
            <Text size="xs" c="dimmed">
              and {tx.output_count - OUTPUTS_LISTED} more
            </Text>
          )}

          {!tx.coinbase && (
            <>
              <Divider color="rgba(255,255,255,0.08)" label={`Inputs (${tx.input_count})`} labelPosition="left" mt="xs" />
              {tx.inputs.map((input, i) => (
                <Group key={i} justify="space-between" gap="xs" wrap="nowrap">
                  <Stack gap={2} style={{ minWidth: 0 }}>
                    <Text size="xs" c="gray.4" style={{ ...MONO, overflowWrap: "anywhere" }}>
                      {input.address ?? "(no address)"}
                    </Text>
                    {input.unconfirmed_parent && (
                      <Group gap={6}>
                        <Badge size="xs" color="yellow" variant="light">
                          unconfirmed parent
                        </Badge>
                        <Anchor href={`/unconfirmed-bitcoin-transaction?txid=${input.txid}`} size="xs" c="orange.4" style={MONO}>
                          check {shortId(input.txid)}
                        </Anchor>
                      </Group>
                    )}
                  </Stack>
                  <Text size="xs" c="white" style={{ ...MONO, whiteSpace: "nowrap" }}>
                    {formatBtc(input.sats)}
                  </Text>
                </Group>
              ))}
              {tx.input_count > tx.inputs.length && (
                <Text size="xs" c="dimmed">
                  and {tx.input_count - tx.inputs.length} more
                </Text>
              )}
            </>
          )}
          <Text size="xs" c="dimmed" mt="xs">
            Checked {formatUtc(Date.parse(tx.fetched_at) / 1000)} via {tx.source === "mempool" ? "mempool.space" : "Blockstream"}.{" "}
            <Anchor
              href={`https://mempool.space/tx/${tx.txid}`}
              target="_blank"
              rel="noopener nofollow"
              size="xs"
              c="dimmed"
              underline="always"
            >
              View on mempool.space
            </Anchor>
          </Text>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default TxResult;
